import { memo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import type { SubAgentState } from '~/types/context';

interface SubAgentProgressProps {
  state: SubAgentState;
}

export const SubAgentProgress = memo(({ state }: SubAgentProgressProps) => {
  const [expanded, setExpanded] = useState(true);
  const total = state.agents.length;
  const runningCount = state.agents.filter((a) => a.status === 'in-progress').length;
  const doneCount = state.agents.filter((a) => a.status === 'complete').length;
  const allDone = state.isComplete;

  const headerText = allDone
    ? `${total} agent${total === 1 ? '' : 's'} · ${doneCount} done`
    : `${total} agent${total === 1 ? '' : 's'} · ${runningCount} running`;

  return (
    <div className="rounded-lg border border-hairline bg-surface overflow-hidden mb-3">
      {/* Header */}
      <div
        className="flex items-center gap-2.5 px-3 py-2.25 cursor-pointer border-b border-hairline"
        onClick={() => setExpanded(!expanded)}
      >
        <span className={`ui-dot ${allDone ? 'ui-dot-ok' : 'ui-dot-live ui-pulsing'}`} />
        <span className="text-primary text-[12px]">{headerText}</span>
        <span
          className={`i-ph:caret-down text-[11px] text-muted ml-auto transition-transform flex-shrink-0 ${expanded ? 'rotate-180' : ''}`}
        />
      </div>

      {/* Agent List */}
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            {state.agents.map((agent) => (
              <SubAgentRow key={agent.id} agent={agent} />
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

function SubAgentRow({ agent }: { agent: SubAgentState['agents'][number] }) {
  const status = agent.status;

  return (
    <motion.div
      className="flex items-start gap-3 px-3 py-2 text-[12px] border-t border-hairline first:border-t-0"
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.15 }}
      layout
    >
      <span className="font-geist-mono text-[11px] text-muted w-[106px] flex-shrink-0 truncate">{agent.id}</span>
      <span className={`flex-1 min-w-0 truncate ${status === 'pending' ? 'text-muted' : 'text-secondary'}`}>
        {agent.task}
        {status === 'in-progress' && agent.toolName && <span className="text-muted"> · {agent.toolName}</span>}
      </span>
      <StatusBadge status={status} />
    </motion.div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'complete') {
    return (
      <span className="flex items-center gap-1 text-success text-[11px] flex-shrink-0">
        <span className="i-ph:check text-[11px]" />
        done
      </span>
    );
  }

  if (status === 'in-progress') {
    return (
      <span className="flex items-center gap-1 text-accent text-[11px] flex-shrink-0">
        <span className="ui-dot ui-dot-live ui-pulsing" />
        running
      </span>
    );
  }

  if (status === 'error') {
    return (
      <span className="flex items-center gap-1 text-danger text-[11px] flex-shrink-0">
        <span className="i-ph:x text-[11px]" />
        failed
      </span>
    );
  }

  return <span className="text-faint text-[11px] flex-shrink-0">queued</span>;
}
